import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Filter } from 'lucide-react';
import { accountsAPI } from '../lib/api';
import { formatCurrency, accountTypeLabels } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const accountTypes = ['asset', 'liability', 'equity', 'revenue', 'expense'];
const currencies = ['CHF', 'EUR', 'USD'];

export default function AccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [deleteAccount, setDeleteAccount] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    account_type: 'expense',
    description: '',
    currency: 'CHF',
    is_active: true
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const res = await accountsAPI.getAll();
      setAccounts(res.data);
    } catch (error) {
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const filteredAccounts = accounts.filter(account => {
    const matchesSearch = 
      account.code.toLowerCase().includes(search.toLowerCase()) ||
      account.name.toLowerCase().includes(search.toLowerCase());
    const matchesType = filterType === 'all' || account.account_type === filterType;
    return matchesSearch && matchesType;
  });

  const groupedAccounts = filteredAccounts.reduce((groups, account) => {
    const type = account.account_type;
    if (!groups[type]) groups[type] = [];
    groups[type].push(account);
    return groups;
  }, {});

  const openCreateDialog = () => {
    setEditingAccount(null);
    setFormData({
      code: '',
      name: '',
      account_type: 'expense',
      description: '',
      currency: 'CHF',
      is_active: true
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (account) => {
    setEditingAccount(account);
    setFormData({
      code: account.code,
      name: account.name,
      account_type: account.account_type,
      description: account.description || '',
      currency: account.currency,
      is_active: account.is_active
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingAccount) {
        // For existing accounts, only update description
        await accountsAPI.updateDescription(editingAccount.id, formData.description);
        toast.success('Account description updated');
      } else {
        await accountsAPI.create(formData);
        toast.success('Account created');
      }
      setIsDialogOpen(false);
      await loadAccounts();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save account';
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    if (!deleteAccount) return;
    try {
      await accountsAPI.delete(deleteAccount.id);
      toast.success('Account deleted');
      setDeleteAccount(null);
      await loadAccounts();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete account';
      toast.error(message);
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'asset': return 'bg-blue-100 text-blue-800';
      case 'liability': return 'bg-purple-100 text-purple-800';
      case 'equity': return 'bg-indigo-100 text-indigo-800';
      case 'revenue': return 'bg-emerald-100 text-emerald-800';
      case 'expense': return 'bg-red-100 text-red-800';
      default: return 'bg-zinc-100 text-zinc-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#D94E18] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              placeholder="Search accounts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="search-accounts"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full sm:w-40" data-testid="filter-type">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {accountTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {accountTypeLabels[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button 
          onClick={openCreateDialog}
          className="bg-[#D94E18] hover:bg-[#C2410C]"
          data-testid="add-account-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Account
        </Button>
      </div>

      {/* Accounts List */}
      {Object.entries(groupedAccounts).map(([type, typeAccounts]) => (
        <Card key={type}>
          <CardHeader className="py-4">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Badge className={getTypeBadgeColor(type)}>
                {accountTypeLabels[type]}
              </Badge>
              <span className="text-zinc-400 text-sm font-normal">
                ({typeAccounts.length})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="w-24">Code</th>
                  <th>Name</th>
                  <th className="hide-mobile">Currency</th>
                  <th className="text-right">Balance</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody>
                {typeAccounts.map((account) => (
                  <tr key={account.id} data-testid={`account-row-${account.id}`}>
                    <td className="font-mono text-zinc-600">{account.code}</td>
                    <td>
                      <span className="font-medium text-zinc-900">{account.name}</span>
                      {account.description && (
                        <p className="text-xs text-zinc-400 mt-0.5 truncate max-w-xs">
                          {account.description}
                        </p>
                      )}
                    </td>
                    <td className="hide-mobile text-zinc-500">{account.currency}</td>
                    <td className="text-right">
                      <span className={`font-mono ${
                        account.balance > 0 ? 'text-emerald-600' : 
                        account.balance < 0 ? 'text-red-600' : 'text-zinc-400'
                      }`}>
                        {formatCurrency(account.balance, account.currency)}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(account)}
                          data-testid={`edit-account-${account.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteAccount(account)}
                          data-testid={`delete-account-${account.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}

      {filteredAccounts.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-zinc-500">No accounts found</p>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingAccount ? 'Edit Account' : 'New Account'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {editingAccount ? (
              /* Edit mode - only description editable */
              <>
                <div className="p-3 bg-zinc-50 rounded-lg">
                  <p className="text-xs text-zinc-500 mb-1">Account Code</p>
                  <p className="font-mono font-medium">{formData.code}</p>
                </div>
                <div className="p-3 bg-zinc-50 rounded-lg">
                  <p className="text-xs text-zinc-500 mb-1">Account Name</p>
                  <p className="font-medium">{formData.name}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-zinc-50 rounded-lg">
                    <p className="text-xs text-zinc-500 mb-1">Type</p>
                    <p className="capitalize">{formData.account_type}</p>
                  </div>
                  <div className="p-3 bg-zinc-50 rounded-lg">
                    <p className="text-xs text-zinc-500 mb-1">Currency</p>
                    <p>{formData.currency}</p>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description"
                    data-testid="account-description-input"
                  />
                  <p className="text-xs text-zinc-400">Only description can be edited for existing accounts</p>
                </div>
              </>
            ) : (
              /* Create mode - all fields editable */
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Code</Label>
                    <Input
                      value={formData.code}
                      onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                      placeholder="5000"
                      data-testid="account-code-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Currency</Label>
                    <Select
                      value={formData.currency}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}
                    >
                      <SelectTrigger data-testid="account-currency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map((curr) => (
                          <SelectItem key={curr} value={curr}>{curr}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Account name"
                    data-testid="account-name-input"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Type</Label>
                  <Select
                    value={formData.account_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, account_type: value }))}
                  >
                    <SelectTrigger data-testid="account-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {accountTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {accountTypeLabels[type]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Description (optional)</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description"
                    data-testid="account-description-input"
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              className="bg-[#D94E18] hover:bg-[#C2410C]"
              data-testid="save-account-btn"
            >
              {editingAccount ? 'Update Description' : 'Create Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteAccount} onOpenChange={() => setDeleteAccount(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteAccount?.name}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
